// Pure arithmetic for the PillNav overflow ("More" dropdown). Kept React-free
// so the "how many tabs fit" rule is unit-testable in the node vitest
// environment (same convention as sku-search.ts / labels.ts).

export interface NavOverflowInput {
  /** Rendered width of every tab, in order. Order never changes. */
  tabWidths: number[];
  /** Rendered width of the trailing "More" pill. */
  moreWidth: number;
  /** Gap between adjacent pills inside the track. */
  gap: number;
  /** Inner width available for pills (track width minus padding + border). */
  available: number;
}

/** Width of the first `n` tabs laid out with `gap` between them. */
function rowWidth(widths: number[], n: number, gap: number): number {
  let total = 0;
  for (let i = 0; i < n; i++) total += widths[i] ?? 0;
  return total + Math.max(0, n - 1) * gap;
}

/**
 * How many LEADING tabs stay visible. Returns `tabWidths.length` when every
 * tab fits without a More pill; otherwise the largest count whose tabs plus a
 * trailing More pill (and the gap before it) still fit, down to 0 (More only).
 * Overflow always takes from the end of the list; order is never changed.
 */
export function visibleTabCount(input: NavOverflowInput): number {
  const { tabWidths, moreWidth, gap, available } = input;
  const n = tabWidths.length;
  if (n === 0) return 0;
  if (rowWidth(tabWidths, n, gap) <= available) return n;
  for (let count = n - 1; count > 0; count--) {
    if (rowWidth(tabWidths, count, gap) + gap + moreWidth <= available) return count;
  }
  return 0;
}
